import { FunctionComponent, Ref } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { Link } from "react-router-dom";

import { format, isSameDay } from "date-fns";

import { faCopy, faEnvelopeOpen } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { IsolatedHtml } from "@/components/IsolatedHtml/IsolatedHtml";
import { classNames } from "@/utils/classNames";
import { messageBodyToHtml } from "@/utils/messageBodyToHtml";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import { ConversationMessageType } from "@/enum/conversation.enum";

import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

import { HideOnErrorImg } from "@/components/HideOnErrorImg/HideOnErrorImg";

import { CalendarInviteCard } from "./CalendarInviteCard";
import { CalendarInviteLine } from "./CalendarInviteLine";
import { CalendarResponseLine } from "./CalendarResponseLine";
import { ConversationProvenanceLine } from "./ConversationProvenanceLine";
import { WrongPersonReferralCta } from "./WrongPersonReferralCta";
import { ScheduledMessageControls } from "./ScheduledMessageControls";
import { Sentiment } from "./Lead/components/Sentiment";
import { EmailMessageContent } from "./Lead/components/EmailMessageContent";
import { renderMessageReceived, renderMessageSent } from "../utils/inboxChatRenderers";
import { resolveCalendarInviteDisplay } from "../utils/resolveCalendarInviteDisplay.util";
import { resolveScheduledFor } from "../utils/resolveScheduledFor.util";

/** "Imported by you" / "Imported by <colleague>" / "Imported" when the importer is unknown. */
const importedLabel = (
	conversation: Conversation,
	currentUserId: number | undefined,
	t: TFunction
): string => {
	if (typeof currentUserId === "number" && conversation.userId === currentUserId) {
		return t("inbox.thread.earlierConversation.importedByYou");
	}

	return conversation.importedByName
		? t("inbox.thread.earlierConversation.importedBy", { name: conversation.importedByName })
		: t("inbox.thread.earlierConversation.imported");
};

/**
 * A single conversation message bubble in the inbox chat log (sent / received /
 * scheduled, with sentiment, open/reaction events and schedule controls).
 * Extracted from {@link useAddInboxChat} so that hook stays under the
 * file-length gate; the lead, the "last received" ref and the two actions are
 * passed in rather than read from the slice.
 */
export const ConversationBubble: FunctionComponent<{
	conversation: Conversation;
	lead?: ListPerson;
	isLastReceived?: boolean;
	lastReceivedRef: Ref<HTMLDivElement>;
	onSaveAsTemplate: (message: string) => void;
	/** The extension renders this subtree in a shadow root; the controls' popups target this. */
	portalContainer?: HTMLElement | null;
}> = ({
	conversation,
	lead,
	isLastReceived,
	lastReceivedRef,
	onSaveAsTemplate,
	portalContainer,
}) => {
	const { t } = useTranslation();
	// Read here rather than threaded through the thread list: it is only ever the signed-in user,
	// and every caller would otherwise have to pass the same value down (PREC-3908).
	const { user } = useAppSelector(authSelector);
	const currentUserId = user?.id;
	const currentListId = lead?.list?.id;
	const isFromOtherList =
		typeof conversation.listId === "number" &&
		typeof currentListId === "number" &&
		conversation.listId !== currentListId;
	// PREC-3901: a calendar client's accept/decline carries nothing the lead wrote — one status
	// line replaces the subject, the body and the sentiment chip.
	const isCalendarResponse =
		conversation.type === "mail_received" && Boolean(conversation.calendarResponse);
	// PREC-3902: an invitation the lead sent us is the same kind of mail — Outlook and Teams write
	// the body, so it renders as the event (a line for a primary mailbox, the card for a secondary
	// one) and never as a message with a sentiment on it.
	const inviteDisplay = resolveCalendarInviteDisplay(conversation);
	const isCalendarMail = isCalendarResponse || inviteDisplay !== "none";

	return (
		<div
			key={conversation.id}
			ref={isLastReceived ? lastReceivedRef : null}
			data-thread-msg=""
			className={classNames(
				"h-fit w-[80%] flex items-start flex-row gap-2",
				conversation.type === "mail_sent" ||
					conversation.type === "message_sent" ||
					conversation.type === "mail_scheduled" ||
					conversation.type === "message_scheduled"
					? "self-end"
					: "self-start"
			)}
		>
			{conversation.type === "mail_received" && lead?.prospect?.image && (
				<div>
					<HideOnErrorImg
						alt={lead?.prospect?.name || ""}
						className="w-10 h-10 rounded-full object-cover"
						src={lead?.prospect?.image || ""}
					/>
				</div>
			)}
			<div className="flex flex-col gap-1 w-full group">
				{isFromOtherList && (
					<span
						className="text-xs text-text-meta bg-brand-white border border-border-light rounded px-2 py-0.5 self-start"
						data-testid="conversation-from-another-list"
					>
						{t("inbox.fromAnotherList")}
					</span>
				)}
				{conversation.importedAt && (
					// PREC-4305: history pulled in from someone's own LinkedIn or mailbox — who, and
					// who can see it (shared with the team, or only the importer on a private lead).
					<span
						className="text-xs text-text-meta bg-brand-white border border-border-light rounded px-2 py-0.5 self-start"
						data-testid="conversation-imported"
						title={t(
							conversation.importedPrivate
								? "inbox.thread.earlierConversation.importedHintPrivate"
								: "inbox.thread.earlierConversation.importedHintShared"
						)}
					>
						{importedLabel(conversation, currentUserId, t)}
					</span>
				)}
				{renderMessageSent(
					conversation,
					resolveScheduledFor(conversation, lead?.schedules),
					t,
					currentUserId
				)}
				{lead && renderMessageReceived(conversation, lead)}
				<ConversationProvenanceLine
					campaignConfig={lead?.list?.campaignConfig}
					conversation={conversation}
					fallbackListId={currentListId}
				/>
				{isCalendarResponse && <CalendarResponseLine conversation={conversation} />}
				{inviteDisplay === "line" && <CalendarInviteLine conversation={conversation} />}
				{!isCalendarMail &&
					(conversation.type === "mail_sent" ||
						conversation.type === "mail_received" ||
						conversation.type === "mail_scheduled") &&
					conversation.subject && (
						<div className="text-sm text-text font-semibold">{conversation.subject}</div>
					)}
				{!isCalendarMail &&
					conversation.messageType === ConversationMessageType.MESSAGE &&
					(conversation.type === "mail_sent" ||
					conversation.type === "mail_scheduled" ||
					conversation.type === "mail_received" ||
					conversation.type === "message_sent" ||
					conversation.type === "message_received" ||
					conversation.type === "message_scheduled" ? (
						<EmailMessageContent
							className={classNames(
								conversation.type === "mail_sent" ||
									conversation.type === "mail_scheduled" ||
									conversation.type === "message_sent" ||
									conversation.type === "message_scheduled"
									? "bg-white text-text"
									: "bg-custom-blue/20 text-text",
								"py-2.5 px-3 rounded-lg text-sm "
							)}
							html={conversation?.message || ""}
						/>
					) : (
						<IsolatedHtml
							className={classNames(
								conversation.type === "message_sent" || conversation.type === "message_scheduled"
									? "bg-white text-text"
									: "bg-custom-blue/20 text-text",
								"py-2.5 px-3 rounded-lg text-sm inline-flex flex-col"
							)}
							html={messageBodyToHtml(conversation?.message?.replaceAll("\\n", ""))}
						/>
					))}
				{inviteDisplay === "card" && <CalendarInviteCard conversation={conversation} />}
				{!isCalendarMail &&
					(conversation.sentiment ||
						conversation.openedAt ||
						conversation.type === "message_sent" ||
						conversation.type === "mail_sent") && (
						<div className="flex flex-row gap-2 justify-between min-h-[28px] h-fit">
							{conversation.sentiment && (
								<div
									className="flex flex-row flex-wrap gap-2"
									data-testid="conversation-sentiment-row"
								>
									<Sentiment conversation={conversation} portalContainer={portalContainer} />
									<WrongPersonReferralCta conversation={conversation} lead={lead} />
								</div>
							)}
							{conversation.openedAt && (
								<div className="text-xs text-gray-700 font-normal flex items-center gap-1">
									<Icon icon={faEnvelopeOpen} />
									Opened at{" "}
									{isSameDay(new Date(conversation.openedAt), new Date()) ? (
										<>Today at {format(new Date(conversation.openedAt), "HH:mm")}</>
									) : (
										format(new Date(conversation.openedAt), "HH:mm dd.MM.yyyy")
									)}
								</div>
							)}
							{conversation.conversationEvents && conversation.conversationEvents?.length > 0 && (
								<div className="text-xs text-gray-700 font-normal flex items-center gap-1">
									{conversation.conversationEvents
										?.filter((e) => e.event === "message_reaction")
										.map((event) => (
											<div
												key={event.id}
												className="bg-white p-1 rounded-lg text-ssm cursor-pointer"
											>
												<Tooltip
													content={
														<div className="flex flex-row gap-1">
															<div>
																<Link target="_blank" to={event.reactionByLinkedinUrl || ""}>
																	{event.reactionBy}
																</Link>
															</div>
															<div>
																{format(
																	new Date(event.createdAt),
																	isSameDay(event.createdAt, new Date())
																		? "HH:mm"
																		: "HH:mm dd.MM.yyyy"
																)}
															</div>
														</div>
													}
												>
													{event.reaction}{" "}
												</Tooltip>
											</div>
										))}
								</div>
							)}
							{conversation.messageType === ConversationMessageType.MESSAGE &&
								(conversation.type === "message_sent" || conversation.type === "mail_sent") && (
									<div className="text-xs text-gray-700 font-normal hidden group-hover:flex items-center gap-1 w-fit">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											image={<Icon className="mr-2" icon={faCopy} />}
											size={ButtonSize.XS}
											title="Save as Template"
											onClick={() => onSaveAsTemplate(conversation.message)}
										/>
									</div>
								)}
						</div>
					)}
				{(conversation.type === "mail_scheduled" || conversation.type === "message_scheduled") && (
					<ScheduledMessageControls
						conversation={conversation}
						portalContainer={portalContainer}
						schedules={lead?.schedules}
					/>
				)}
			</div>
		</div>
	);
};
